<template>
  <div class="runner-referrer-page">
    <div class="filter-bar">
      <div class="search-box">
        <input
          type="text"
          v-model="userSearchKey"
          placeholder="搜索推广人姓名/手机号..."
          class="search-input"
        />
        <div class="user-search-wrapper">
          <div
            v-if="showUserDropdown && filteredUsers.length > 0"
            class="user-dropdown"
          >
            <div
              v-for="user in filteredUsers"
              :key="user.id"
              class="dropdown-item"
              @click="selectSearchUser(user)"
            >
              {{ user.real_name }} ({{ user.phone }})
            </div>
          </div>
        </div>
        <button @click="search" class="search-btn">搜索</button>
        <span v-if="selectedSearchUser" class="search-hint">
          已筛选: {{ selectedSearchUser.name }} ({{ selectedSearchUser.phone }})
          <span class="clear-filter" @click="clearSearchUser">×</span>
        </span>
      </div>
      <div class="filter-selects">
        <select v-model="status" class="filter-select" @change="search">
          <option value="">全部状态</option>
          <option value="1">启用</option>
          <option value="0">禁用</option>
        </select>
        <button @click="openAddModal" class="add-btn">+ 添加跑手推广人</button>
      </div>
    </div>

    <div class="table-container">
      <div v-if="loading" class="loading-state">加载中...</div>
      <table v-else class="referrer-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>姓名</th>
            <th>手机号</th>
            <th>开始日期</th>
            <th>结束日期</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in referrers" :key="item.id">
            <td>{{ item.id }}</td>
            <td>{{ item.realName }}</td>
            <td>{{ item.userPhone }}</td>
            <td>{{ item.startDate || '-' }}</td>
            <td>{{ item.endDate || '长期有效' }}</td>
            <td>
              <span :class="'status-tag ' + (item.status === 1 ? 'enabled' : 'disabled')">
                {{ item.status === 1 ? '启用' : '禁用' }}
              </span>
            </td>
            <td>
              <button @click="goDetail(item)" class="action-btn view">查看</button>
              <button @click="openEditModal(item)" class="action-btn edit">编辑</button>
              <button @click="handleDelete(item)" class="action-btn delete-btn">删除</button>
            </td>
          </tr>
          <tr v-if="referrers.length === 0">
            <td colspan="7" class="empty-row">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1 || loading" @click="changePage(currentPage - 1)" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages || loading" @click="changePage(currentPage + 1)" class="page-btn">下一页</button>
    </div>

    <!-- 添加/编辑弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ isEdit ? '编辑推荐人' : '添加推广人' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <form @submit.prevent="submitForm" class="modal-form">
          <div class="form-item" v-if="!isEdit">
            <label>选择用户 *</label>
            <div class="search-select-container">
              <input
                type="text"
                v-model="formUserSearchKey"
                placeholder="搜索用户..."
                class="search-select-input"
                @focus="showFormUserDropdown = true"
              />
              <div class="search-select-arrow" @click="showFormUserDropdown = !showFormUserDropdown">▼</div>
              <div v-if="showFormUserDropdown" class="search-select-dropdown">
                <div
                  v-for="user in filteredFormUsers"
                  :key="user.id"
                  :class="'dropdown-item' + (form.userId === user.id ? ' selected' : '')"
                  @click="selectFormUser(user)"
                >
                  {{ user.real_name }} ({{ user.phone }})
                </div>
                <div v-if="filteredFormUsers.length === 0" class="dropdown-empty">
                  未找到匹配的用户
                </div>
              </div>
            </div>
            <div v-if="form.userId" class="selected-user">
              已选择: {{ getSelectedFormUserName() }}
            </div>
          </div>
          <div class="form-item">
            <label>开始日期</label>
            <input type="date" v-model="form.startDate" class="form-input" />
          </div>
          <div class="form-item">
            <label>结束日期</label>
            <input type="date" v-model="form.endDate" class="form-input" />
            <span class="form-hint">留空表示长期有效</span>
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model="form.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="modal-footer">
            <button type="button" @click="closeModal" class="btn-cancel">取消</button>
            <button type="submit" class="btn-submit">{{ isEdit ? '保存修改' : '确认添加' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getRunnerReferrerList,
  createRunnerReferrer,
  updateRunnerReferrer,
  deleteRunnerReferrer
} from '@/api/runner-referrers'
import { getUserList } from '@/api'

export default {
  name: 'RunnerReferrer',
  data() {
    return {
      userSearchKey: '',
      selectedSearchUser: null,
      showUserDropdown: false,
      status: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      loading: false,
      referrers: [],
      users: [],
      showModal: false,
      isEdit: false,
      editId: null,
      formUserSearchKey: '',
      showFormUserDropdown: false,
      form: {
        userId: null,
        startDate: '',
        endDate: '',
        status: 1
      }
    }
  },
  computed: {
    filteredUsers() {
      if (!this.userSearchKey) return []
      const key = this.userSearchKey.toLowerCase()
      return this.users.filter(
        u =>
          u.name.toLowerCase().includes(key) ||
          u.phone.includes(key)
      )
    },
    filteredFormUsers() {
      if (!this.formUserSearchKey) return this.users
      const key = this.formUserSearchKey.toLowerCase()
      return this.users.filter(
        u =>
          u.name.toLowerCase().includes(key) ||
          u.phone.includes(key)
      )
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.loadUsers()
    this.loadData()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(e) {
      if (this.showUserDropdown && !e.target.closest('.search-box')) {
        this.showUserDropdown = false
      }
      if (this.showFormUserDropdown && !e.target.closest('.search-select-container')) {
        this.showFormUserDropdown = false
      }
    },
    async loadUsers() {
      try {
        const response = await getUserList()
        if (response && response.data) {
          const pageData = response.data
          const list = pageData.data || pageData.list || []
          this.users = list.map(u => ({
            id: u.id,
            name: u.real_name || u.nickname || u.name || '',
            real_name: u.real_name || '',
            phone: u.phone || ''
          }))
        }
      } catch (error) {
      }
    },
    async loadData() {
      try {
        this.loading = true
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          status: this.status || undefined
        }
        if (this.selectedSearchUser) {
          params.user_id = this.selectedSearchUser.id
        }
        const response = await getRunnerReferrerList(params)
        if (response && response.code === 0 && response.data) {
          const d = response.data
          const list = d.list || d.data || []
          this.referrers = list.map(item => ({
            id: item.id,
            userId: item.user_id,
            userName: item.user?.name || item.user?.nickname || item.user?.username || '-',
            realName: item.user?.real_name || '-',
            userPhone: item.user?.phone || '-',
            startDate: this.formatDate(item.start_date),
            endDate: this.formatDate(item.end_date),
            status: item.status,
            createdAt: this.formatDateTime(item.created_at)
          }))
          this.totalPages = d.total
            ? Math.ceil(d.total / this.pageSize)
            : (d.last_page || 1)
        }
      } catch (error) {
        alert('加载推广人列表失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    selectSearchUser(user) {
      this.selectedSearchUser = user
      this.userSearchKey = ''
      this.showUserDropdown = false
    },
    clearSearchUser() {
      this.selectedSearchUser = null
      this.currentPage = 1
      this.loadData()
    },
    search() {
      this.currentPage = 1
      this.loadData()
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadData()
      }
    },
    goDetail(item) {
      this.$router.push({ path: `/runner-referrer/detail/${item.id}` })
    },
    openAddModal() {
      this.isEdit = false
      this.editId = null
      this.form = { userId: null, startDate: '', endDate: '', status: 1 }
      this.formUserSearchKey = ''
      this.showFormUserDropdown = false
      this.showModal = true
    },
    openEditModal(item) {
      this.isEdit = true
      this.editId = item.id
      this.form = {
        userId: item.userId,
        startDate: item.startDate === '-' ? '' : item.startDate,
        endDate: item.endDate === '长期有效' ? '' : item.endDate,
        status: item.status
      }
      this.showModal = true
    },
    selectFormUser(user) {
      this.form.userId = user.id
      this.formUserSearchKey = ''
      this.showFormUserDropdown = false
    },
    getSelectedFormUserName() {
      const user = this.users.find(u => u.id === this.form.userId)
      return user ? `${user.real_name} (${user.phone})` : ''
    },
    async submitForm() {
      try {
        if (!this.isEdit && !this.form.userId) {
          alert('请选择用户')
          return
        }
        const data = {
          start_date: this.form.startDate || null,
          end_date: this.form.endDate || null,
          status: this.form.status
        }
        if (!this.isEdit) {
          data.user_id = this.form.userId
          const response = await createRunnerReferrer(data)
          if (response && response.code === 0) {
            alert('添加成功')
          } else {
            alert(response?.message || '添加失败')
            return
          }
        } else {
          const response = await updateRunnerReferrer(this.editId, data)
          if (response && response.code === 0) {
            alert('修改成功')
          } else {
            alert(response?.message || '修改失败')
            return
          }
        }
        this.closeModal()
        this.loadData()
      } catch (error) {
        alert('操作失败，请稍后重试')
      }
    },
    async handleDelete(item) {
      const name = item.userName
      if (!confirm(`确定要删除推广人「${name}」吗？`)) return
      try {
        const response = await deleteRunnerReferrer(item.id)
        if (response && response.code === 0) {
          alert('删除成功')
          this.loadData()
        } else {
          alert(response?.message || '删除失败')
        }
      } catch (error) {
        alert('删除失败，请稍后重试')
      }
    },
    closeModal() {
      this.showModal = false
      this.isEdit = false
      this.editId = null
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.runner-referrer-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.user-search-wrapper {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: 250px;
}

.user-dropdown {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
}

.user-dropdown .dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
}

.user-dropdown .dropdown-item:hover {
  background: #f5f7fa;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.search-hint {
  font-size: 13px;
  color: #2ecc71;
  background: #e8f5e9;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.clear-filter {
  margin-left: 6px;
  cursor: pointer;
  color: #999;
  font-weight: bold;
}

.clear-filter:hover {
  color: #e74c3c;
}

.filter-selects {
  display: flex;
  gap: 15px;
  align-items: center;
}

.filter-select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.loading-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.referrer-table {
  width: 100%;
  border-collapse: collapse;
}

.referrer-table th,
.referrer-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.referrer-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.empty-row {
  text-align: center;
  color: #999;
  padding: 30px !important;
}

.status-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status-tag.enabled {
  background: #e8f5e9;
  color: #2ecc71;
}

.status-tag.disabled {
  background: #ffebee;
  color: #e74c3c;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.edit {
  background: #f39c12;
  color: #fff;
}

.action-btn.view {
  background: #3498db;
  color: #fff;
}

.action-btn.delete-btn {
  background: #e74c3c;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 450px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.modal-form {
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}

.form-input,
.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3498db;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.selected-user {
  margin-top: 8px;
  padding: 8px 12px;
  background: #e8f5e9;
  border-radius: 4px;
  font-size: 13px;
  color: #2ecc71;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 10px 20px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-submit {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.search-select-container {
  position: relative;
  width: 100%;
}

.search-select-input {
  width: 100%;
  padding: 10px 35px 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.search-select-input:focus {
  outline: none;
  border-color: #3498db;
}

.search-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 12px;
  cursor: pointer;
}

.search-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
}

.dropdown-item:hover {
  background: #f5f7fa;
}

.dropdown-item.selected {
  background: #e8f4fd;
  color: #3498db;
}

.dropdown-empty {
  padding: 15px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
